import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon, type IconName } from "./Icon";

interface ToastInput {
  text: string;
  /** The icon before the words; a tick by default. */
  icon?: IconName;
  /** A refusal or an error: the info icon, in the error colour, so it never looks like something that worked. */
  error?: boolean;
  /** One action, such as Undo. A toast with an action stays a little longer. */
  action?: { label: string; onClick: () => void };
}

const SHORT_MS = 2800;
const WITH_ACTION_MS = 5200;

type Show = (toast: ToastInput | string) => void;
const ToastContext = createContext<Show | null>(null);

/**
 * The app's one toast (`.toast`): a short line at the top, one at a time, gone on its own. Every screen shows
 * its toasts through `useToast()`.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<(ToastInput & { key: number }) | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  const show = useCallback<Show>((input) => {
    const t = typeof input === "string" ? { text: input } : input;
    if (timer.current) clearTimeout(timer.current);
    seq.current += 1;
    setToast({ ...t, key: seq.current });
    timer.current = setTimeout(() => setToast(null), t.action ? WITH_ACTION_MS : SHORT_MS);
  }, []);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const value = useMemo(() => show, [show]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast
        ? createPortal(
            <div key={toast.key} className={toast.error ? "toast err" : "toast"} role="status" aria-live="polite">
              <Icon name={toast.icon ?? (toast.error ? "info" : "check")} />
              <span>{toast.text}</span>
              {toast.action ? (
                <button
                  type="button"
                  className="link toastlink"
                  onClick={() => {
                    setToast(null);
                    toast.action?.onClick();
                  }}
                >
                  {toast.action.label}
                </button>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

/** Shows a toast. Outside the provider (a test, a page with none) it does nothing rather than fail. */
export function useToast(): Show {
  return useContext(ToastContext) ?? noop;
}

function noop() {}
